import c from 'classnames';
import { Suspense, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useMatch } from 'react-router-dom';

import { Icon, Spinfinity, Typography } from '@repo/ui';
import { kitResultsViewable } from '@repo/utils';
import { useScrollDisplay } from 'hooks/useScrollDisplay';
import useRequestPDFReport from 'services/pdfReport/useRequestPDFReport';
import { getImpersonateId } from 'store/user';

import { DesignButton } from 'components';
import Breadcrumb from 'components/Breadcrumb/Breadcrumb';
import { KitSelectV2 } from 'components/THSelect/KitSelect';

import VersionPill from 'components/VersionPill/VersionPill';
import useKitSystemInfo from 'services/system-info/useKitSystemInfo';
import styles from './Header.module.scss';

type HeaderProps = {
  title?: string;
  type?: 'simple' | 'full';
  containerRef?: React.RefObject<HTMLElement>;
};

const HeaderPdfButtons = ({ kitId }: { kitId: string }) => {
  const { signedUrl } = useRequestPDFReport(kitId);

  const viewPDFReport = useCallback(async () => {
    if (signedUrl) window.open(signedUrl, '_blank');
  }, [signedUrl]);

  return (
    <>
      <div className={c('hide-on-mobile', styles.infoItem)}>
        <DesignButton
          color='grey'
          label='Practitioner PDF'
          size='small'
          className={styles.pdfButton}
          iconNameLeft='download'
          onClick={viewPDFReport}
        />
      </div>
      <div className={c('hide-on-desktop', styles.infoItem)}>
        <DesignButton
          color='grey'
          label='PDF'
          size='small'
          className={styles.pdfButton}
          iconNameLeft='download'
          onClick={viewPDFReport}
        />
      </div>
    </>
  );
};

const HeaderFull = ({
  title,
  containerRef,
}: {
  title?: string;
  containerRef?: React.RefObject<HTMLElement>;
}) => {
  const match = useMatch<'page' | 'kitId', string>('/:page/:kitId/*');
  const page = match?.params?.page;
  const kitId = match?.params?.kitId;

  const { pathname } = useLocation();

  const impersonateId = useSelector(getImpersonateId);

  const isConsultsInnerPage = pathname.startsWith('/consults/');
  const isTopLevelKitRoute =
    (page === 'results' || page === 'action-plan') && !isConsultsInnerPage;

  const { currentKit, isUpdating, prettyVersion } = useKitSystemInfo(kitId);
  const currentKitHasResults =
    !!currentKit && kitResultsViewable(currentKit, !!impersonateId);

  const isVisible = useScrollDisplay({
    threshold: 50,
    action: 'hide',
    scrollableElRef: containerRef,
  });
  const [displayNone, setDisplayNone] = useState(false);

  useEffect(() => {
    if (isVisible) {
      setDisplayNone(false);
    }
  }, [isVisible]);

  if (!kitId || !currentKit) {
    return null;
  }

  const showPdfButtons =
    !!currentKit?.date_survey_completed && currentKitHasResults;

  return (
    <header className={c(styles.header, styles.full)} data-id='header'>
      <Breadcrumb className={styles.breadcrumb} />
      <div
        className={c(
          isTopLevelKitRoute ? styles.topLevelTitleRow : styles.innerTitleRow,
        )}
      >
        <div className={styles.title}>
          <Typography
            variant={isTopLevelKitRoute ? 'heading-xl' : 'heading-m'}
            className='my-auto'
            responsive
          >
            {title}
          </Typography>
        </div>
        {isTopLevelKitRoute && (
          <div
            className={c(
              styles.mobileKitSelectContainer,
              !isVisible && styles.hidden,
              displayNone && styles.displayNone,
            )}
            onTransitionEnd={() => {
              if (!isVisible) setDisplayNone(true);
            }}
          >
            <KitSelectV2 />
          </div>
        )}
        {isTopLevelKitRoute && (
          <div className={styles.bottom}>
            <div className='hide-on-mobile'>
              <KitSelectV2 />
            </div>
            <div className={styles.infoItem}>
              <Icon name='priceTagFilled' size='s' />
              <Typography variant='label'>{`Kit ${currentKit.id}`}</Typography>
            </div>
            <div className={styles.infoItem}>
              <Typography variant='label'>{currentKit.sample_label}</Typography>
            </div>
            {(isUpdating || prettyVersion) && (
              <div className={c('hide-on-mobile', styles.infoItem)}>
                <VersionPill />
              </div>
            )}
            {showPdfButtons && (
              <Suspense
                fallback={
                  <div className={styles.infoItem}>
                    <Spinfinity size={30} />
                  </div>
                }
              >
                <HeaderPdfButtons kitId={kitId} />
              </Suspense>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

export const Header = ({
  title,
  type = 'simple',
  containerRef,
}: HeaderProps) => {
  if (type === 'simple') {
    return (
      <>
        {title && (
          <div
            data-id='header'
            className={c(
              styles.header,
              styles.simple,
              !title && 'hide-on-mobile',
            )}
          >
            <Typography variant='heading-m' className={styles.title} responsive>
              {title}
            </Typography>
          </div>
        )}
      </>
    );
  }

  if (type === 'full') {
    return (
      <>
        <HeaderFull title={title} containerRef={containerRef} />
      </>
    );
  }

  return null;
};

export default Header;
